import { Fragment, useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import {
  DisplayOptaFusData,
  DisplayOptaFloodscoreData,
  DisplayOptaCommercialPerilData,
  DisplayOptaIclarifyCommercialConstructionData,
} from './Displays';
import { Pagebreak } from '../utils';
import { useLazyQuery } from '@apollo/client';
import { LOCATIONS_FROM_QUOTE_HASH } from '../../../../queries';

// classes and types
import type { $TSFixMe } from '@calefy-inc/utilityTypes';
import type { RouteComponentProps } from '@reach/router';

const A4_PAPER_WIDTH = '210mm';
const useDetailsForSingleLocationStyles = makeStyles((_theme: $TSFixMe) => ({
  printingPaper: {
    width: A4_PAPER_WIDTH,
  },
}));

interface OptaDetailsForSingleLocationProps {
  location: $TSFixMe;
}

const OPTA_DATA_TYPES = [
  'optaFusData',
  'optaFloodscoreData',
  'optaCommercialPerilScoreData',
  'optaIclarifyCommercialConstructionData',
] as const;

export const locationHasOptaDetails = (location: $TSFixMe) => {
  if (!location?.details?.opta) {
    return false;
  }

  return OPTA_DATA_TYPES.some((type) => location.details.opta[type]);
};

export const OptaDetailsForSingleLocation = ({
  location,
}: OptaDetailsForSingleLocationProps) => {
  const classes = useDetailsForSingleLocationStyles();

  if (!locationHasOptaDetails(location)) {
    return null;
  }

  const sections = [
    location.details.opta.optaFusData
      ? {
          key: 'optaFusData',
          section: <DisplayOptaFusData location={location} />,
        }
      : null,
    location.details.opta.optaFloodscoreData
      ? {
          key: 'optaFloodscoreData',
          section: <DisplayOptaFloodscoreData location={location} />,
        }
      : null,
    location.details.opta.optaCommercialPerilScoreData
      ? {
          key: 'optaCommercialPerilScoreData',
          section: <DisplayOptaCommercialPerilData location={location} />,
        }
      : null,
    location.details.opta.optaIclarifyCommercialConstructionData
      ? {
          key: 'optaIclarifyCommercialConstructionData',
          section: (
            <DisplayOptaIclarifyCommercialConstructionData
              location={location}
            />
          ),
        }
      : null,
  ].filter(
    (section): section is { key: string; section: JSX.Element } =>
      section !== null,
  );

  return (
    <div className={classes.printingPaper}>
      {sections.map(({ key, section }, index) => (
        <Fragment key={key}>
          {section}
          {index < sections.length - 1 ? <Pagebreak /> : null}
        </Fragment>
      ))}
    </div>
  );
};

interface OptaPdfProps extends RouteComponentProps {
  hash?: string;
}
export const OptaPdf = ({ hash }: OptaPdfProps) => {
  const [locations, setLocations] = useState<Array<$TSFixMe>>([]);
  const [getDetails, { error, data }] = useLazyQuery(LOCATIONS_FROM_QUOTE_HASH);

  useEffect(() => {
    if (hash) {
      getDetails({
        variables: {
          hash,
        },
      });
    }
  }, [hash, getDetails]);

  useEffect(() => {
    if (data?.locationsFromQuoteHash) {
      setLocations(data.locationsFromQuoteHash);
    }
  }, [data]);

  if (error) {
    return null;
  }

  return (
    <div data-present='true'>
      {locations.map((location, index) => (
        <OptaDetailsForSingleLocation key={index} location={location} />
      ))}
    </div>
  );
};
